import type { WelcomeDialogSchemaType } from "@configs/custom-app-config-schema";
import i18n from "@helpers/lib/i18n";
import { interpolateString } from "@helpers/string-utils";
import { formatCount } from "@memorang/helpers/common";
import type { BaseItemSchema, SessionType } from "@memorang/types";

export type DialogType =
	| "pause"
	| "completeSection"
	| "completeTest"
	| "timerExpired";

const getAverageTimePerQuestion = (mins: number, numTotalQuestions: number) => {
	return ((mins * 60) / numTotalQuestions).toFixed(0);
};

export const getBreakDialogConfig = (breakSeconds: number) => {
	const breakMinutes = Math.floor(breakSeconds / 60);
	return {
		title: "Would you like to take a break?",
		icon: "timer-sand-paused",
		content: `You’ve been focused for a while. A **${breakMinutes}-minute** break can help you stay sharp. Your exam timer will pause and you can continue when you feel ready.`,
		buttons: [
			{
				label: "Continue exam",
				type: "cancel" as const,
			},
			{
				label: "Start break",
				type: "confirm" as const,
			},
		],
	};
};

export const getBreakExpiredDialogConfig = () => {
	return {
		title: "Break Time Expired",
		icon: "timer-sand-complete",
		content:
			"Your break time has expired, and the test has automatically resumed. You can now continue with your test.",
		buttons: [
			{
				label: "Continue",
				type: "confirm" as const,
			},
		],
	};
};

export const getBreakOngoingDialogConfig = () => {
	return {
		title: "You're on a scheduled break",
		icon: "pause",
		content: `Your exam will resume automatically when the break ends. You may return early if ready.  
		Please do not refresh or leave this page.`,
		buttons: [
			{
				label: "Resume exam",
				type: "confirm" as const,
			},
		],
	};
};

export const constructExamResourcesMarkdown = (links: string[]) => {
	const linksMarkdown = links.map((link) => `- ${link}`).join("\n");
	return `#### Study Resources\n\n${linksMarkdown}`;
};
export const helpDialog = ({
	sessionItems,
	totalTime,
	examName,
	isDiagnosticTest,
	numSections,
	numTotalQuestions,
	diagnosticLabel,
	studyResourcesMarkdown,
}: {
	sessionItems: BaseItemSchema[];
	totalTime: number;
	examName: string;
	isDiagnosticTest: boolean;
	numSections: number;
	numTotalQuestions: number;
	diagnosticLabel?: Record<string, string>;
	studyResourcesMarkdown?: string;
}) => {
	const numQuestions = sessionItems.length;
	const mins = totalTime / 60;

	const averageTimePerQuestion = getAverageTimePerQuestion(
		mins,
		numTotalQuestions,
	);

	return {
		triggerButton: {
			label: "Help",
			icon: "help-circle-outline",
		},
		title: "Help",
		icon: "help-circle-outline",
		body: `
  ${studyResourcesMarkdown ? `${studyResourcesMarkdown}\n\n` : ""}#### Overview
  ${
		isDiagnosticTest
			? `* You're taking a ${diagnosticLabel ? diagnosticLabel[i18n.language] : "Diagnostic"} test.`
			: `* The exam is the ${examName}, with ${numTotalQuestions} items.`
	}
  ${
		numSections > 1
			? `* This test has ${numSections} sections of ${numQuestions} questions each.\n  * There are a total of ${numTotalQuestions} questions.`
			: ""
	}
  * After finishing, you'll receive a detailed score report.
  #### Time
  * Total time: ${Number((mins / 60).toFixed(2))} hours (${mins.toFixed(0)} minutes).
  * Average: ~${averageTimePerQuestion} seconds per question.
  * Remaining time is always shown in the top right.
  #### Answering Questions
  * Select the best answer.
  * Right-click an option to cross it out.
  #### Navigation
  * Use **Next**, **Prev**, or **Skip** to move between questions.
  * ${
		numSections > 1
			? "You can freely navigate within a section, but once you move to the next section you cannot go back."
			: "You can freely jump to any question."
	}
  #### Tools
  * **Highlighter:** Available in the top-left toolbar. Use it to highlight text in the question stem.
  * **Cross Out:** Right-click an answer to mark it as eliminated.
  `,
		confirmCta: "Close",
	};
};

export const getDialogSessionConfigs = ({
	currentSection,
	numTotalSections,
}: {
	currentSection?: number;
	numTotalSections?: number;
}): Record<
	DialogType,
	{
		title: string;
		icon: string;
		content: string;
		buttons?: {
			label: string;
			type: "confirm" | "cancel";
		}[];
	}
> => ({
	pause: {
		title: "Pause Test",
		icon: "pause-circle-outline",
		content: "Are you sure you want to pause this test? You can resume later",
		buttons: [
			{
				label: "Cancel",
				type: "cancel",
			},
			{
				label: "Confirm",
				type: "confirm",
			},
		],
	},
	completeSection: {
		title: "Complete Section",
		icon: "check-outline",
		content:
			numTotalSections && currentSection
				? `You have completed **section ${currentSection} of ${numTotalSections}**. Once you move on to the **next section** you will not be able to go back to view or review your answers.`
				: "You have completed all the items in this section. Once you move on to the **next section** you will not be able to go back to view or review your answers.",
		buttons: [
			{
				label: "Cancel",
				type: "cancel",
			},
			{
				label: "Next Section",
				type: "confirm",
			},
		],
	},
	completeTest: {
		title: "Finish Test",
		icon: "flag-checkered",
		content:
			"You have attempted all items in this practice test. Click **finish** to end your test and see your results.",
		buttons: [
			{
				label: "Cancel",
				type: "cancel",
			},
			{
				label: "Finish",
				type: "confirm",
			},
		],
	},
	timerExpired: {
		title: "Time Expired",
		icon: "timer-sand-complete",
		content: "Your time has expired and your test will now be submitted.",
	},
});

export const getSessionStartDialogConfig = ({
	examName,
	totalTime,
	isQuiz,
	numSections,
	numTotalQuestions,
	diagnosticLabel,
	welcomeDialog,
}: {
	examName: string;
	totalTime: number;
	isQuiz?: boolean;
	numSections: number;
	numTotalQuestions: number;
	diagnosticLabel?: Record<string, string>;
	welcomeDialog?: WelcomeDialogSchemaType;
}) => {
	const mins = totalTime / 60;

	const averageTimePerQuestion = getAverageTimePerQuestion(
		mins,
		numTotalQuestions,
	);
	const topic =
		examName.split(": ").length > 1 ? examName.split(": ")[1] : examName;

	const fullLengthText =
		numSections > 1 && !isQuiz
			? `In this **${examName}**, you will be tasked with completing **${formatCount(
					"section",
					numSections,
					true,
				)}** and a total of **${numTotalQuestions} questions**`
			: `In this${
					isQuiz ? "" : ""
				} **${examName}**, you will be tasked with completing **${numTotalQuestions} questions**`;
	const contentQuiz = `Welcome to your ${
		diagnosticLabel
			? diagnosticLabel[i18n.language]
			: "**diagnostic assessment**"
	} on the topic of **${topic.trim()}**.  
  The total time allotted to complete the exam is ${Number(
		(mins / 60).toFixed(2),
	)} hours, or ${mins.toFixed(0)} minutes.  
  You have an average of ${averageTimePerQuestion} seconds per question.  
  Feel free to navigate between questions as you work through them.  
  Once you have completed the test you will receive a score report with an analysis of your results. Good luck!`;
	return {
		title:
			isQuiz && welcomeDialog?.diagnostic.title
				? interpolateString(welcomeDialog.diagnostic.title, {
						examName,
					})
				: examName,
		icon: "clipboard-check-outline",
		content: isQuiz
			? welcomeDialog?.diagnostic.body
				? interpolateString(welcomeDialog.diagnostic.body, {
						hr: (mins / 60).toFixed(2),
						min: mins.toFixed(0),
						sec: averageTimePerQuestion,
					})
				: contentQuiz
			: `${fullLengthText}.   
You can freely navigate between questions${
					numSections > 1
						? " within a section, but once you move to the next section you cannot go back"
						: ""
				}.  
        The total time allotted to complete the exam is **${Number(
					(mins / 60).toFixed(2),
				)} hours, or ${mins.toFixed(0)} minutes**.  
        You have an average of **${averageTimePerQuestion} seconds** per question.  
        Once you have completed the test you will receive a score report with an analysis of your results. Good luck!
				${welcomeDialog?.disclaimer ? `\n\\\n${welcomeDialog.disclaimer}` : ""}`,
		buttons: [
			{
				label: "Continue",
				type: "confirm" as const,
			},
		],
	};
};

export const getExitDialogConfig = ({
	isOlx,
}: { isOlx?: boolean }): Record<
	SessionType,
	{
		title: string;
		body: string;
		dismissCta: string;
		confirmCta: string;
	}
> => ({
	FORMATIVE: {
		title: "End session?",
		body: "Your progress will be saved. You can start a new session later to continue practicing.",
		dismissCta: "Exit",
		confirmCta: "Continue practicing",
	},
	SUMMATIVE: isOlx
		? {
				title: "Exit while timer runs?",
				body: "Your timer will continue running even if you leave this page.",
				dismissCta: "Exit anyway",
				confirmCta: "Stay",
			}
		: {
				title: "Pause assessment?",
				body: "Your timer will pause, and you can resume this assessment anytime.",
				dismissCta: "Pause & exit",
				confirmCta: "Continue",
			},
});
